<template>
  <div :class="['memlist-text-input-container', styleClass, 'mt-2', 'ml-text-area']">

    <!-- horizontal layout -------------------------------------------------->
    <div v-if="layout === 'horizontal'">
      <div v-if="title" class="horizontal1">
        <label class="ml-text-label">{{ title }}</label>
      </div>
      <div class="horizontal2">
        <textarea
          ref="textArea"
          :id="id"
          @input="on_input"
          :value="local_value"
          :placeholder="placeholder"
          :disabled="disabled"
          :style="computedStyle"
          :class="[state_class, inputStyleClass]"
          :rows="rows"
        ></textarea>
        <div
          class="error-text"
          v-if="required && invalid_text && invalid_text.length > 0 && local_state === false"
        >
          {{ invalid_text }}
        </div>
      </div>
    </div>

    <!-- vertical layout ---------------------------------------------------->
    <div v-if="layout === 'vertical'">
      <div v-if="title" class="vertical1">
        <label class="ml-text-label">{{ title }}</label>
      </div>
      <div class="vertical2">
        <textarea
          ref="textArea"
          :id="id + '-v'"
          @input="on_input"
          :value="local_value"
          :placeholder="placeholder"
          :disabled="disabled"
          :style="computedStyle"
          :class="[state_class, inputStyleClass]"
          :rows="rows"
        ></textarea>
        <div
          class="error-text"
          v-if="required && invalid_text && invalid_text.length > 0 && local_state === false"
        >
          {{ invalid_text }}
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';
import { randomstr } from '@/core/services/deviceId';

export default {
  name: 'MemlistTextArea',

  mixins: [toasts],
  emits: ['input', 'change', 'validated', 'submit'],

  props: {
    /* visual --------------------------------------------------------------*/
    size: {
      type: String,
      default: 'sm',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    variation: {
      type: String,
      default: 'normal',
    },
    layout: {
      type: String,
      default: 'horizontal',
    },
    title: {
      type: String,
      default: '',
    },

    /* data / validation ---------------------------------------------------*/
    name: {
      type: String,
      default: '',
    },
    value: [String, Number],
    placeholder: {
      type: String,
      default: '',
    },
    rows: {
      type: [String, Number],
      default: 3,
    },

    validate: {
      type: Number,
      default: 0,
    },
    required: {
      type: Boolean,
      default: false,
    },
    validation_type: {
      type: String,
      default: 'TEXT',
    },
    invalid_text: {
      type: String,
      default: '',
    },
    validation_function: {
      type: Function,
      default: null,
    },

    /* behaviour -----------------------------------------------------------*/
    disabled: {
      type: Boolean,
      default: false,
    },
    autoexpand: {
      type: Boolean,
      default: false,
    },
    submit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      id: randomstr(8),
      local_state: null,
      local_value: null,
    };
  },

  computed: {
    //---------------------------------------------------------------------
    // visual helpers (mirrors MemlistTextInput "computedStyle" et al.)
    //---------------------------------------------------------------------
    computedStyle() {
      const sizes = {
        sm: { fontSize: '14px', padding: '6px 10px', minHeight: '48px' },
        md: { fontSize: '16px', padding: '8px 12px', minHeight: '56px' },
        lg: { fontSize: '18px', padding: '10px 14px', minHeight: '60px' },
      };
      return sizes[this.size] || sizes.sm;
    },
    inputStyleClass() {
      return this.variation === 'enhanced' ? 'enhanced-style' : '';
    },
    styleClass() {
      return this.variation === 'enhanced' ? 'enhanced-container' : '';
    },
    state_class() {
      if (!this.required) return 'memlist-text-input';
      if (this.local_state !== null) {
        return this.local_state ? 'memlist-text-input success1' : 'memlist-text-input failure1';
      }
      return 'memlist-text-input';
    },
  },

  watch: {
    validate(newVal) {
      if (newVal !== 0) {
        this.local_state = this.validate_input();
      }
    },
    value: {
      handler(val) {
        this.local_value = val;
        this.$nextTick(this.do_autoexpand);
      },
      immediate: true,
    },
  },

  mounted() {
    // auto‑expand once mounted & bind key‑press for optional submit
    this.$nextTick(() => {
      const textarea = this.$refs.textArea;
      if (textarea) {
        textarea.addEventListener('keypress', this.on_keypress);
        this.do_autoexpand();
      }
    });
  },

  methods: {
    // ------------------------------------------------------------ events --
    on_keypress(e) {
      if (this.submit && e.which === 13) {
        e.preventDefault();
        this.$emit('submit');
      }
    },
    on_input(e) {
      const val = e.target.value;
      this.local_value = val;
      this.local_state = this.validate_input();
      this.$emit('input', val);
      this.do_autoexpand();
    },

    // ------------------------------------------------------------ helpers --
    validate_input() {
      // TODO: plug in real validation logic (same contract as TextInput)
      return true;
    },
    do_autoexpand() {
      if (!this.autoexpand) return;
      const el = this.$refs.textArea;
      if (!el) return;
      el.style.height = '';
      el.style.height = Math.min(el.scrollHeight + 10, 300) + 'px';
    },
  },
};
</script>

<style scoped lang="css">
textarea:focus {
  outline: none;
  box-shadow: 0 1px 0 #3968ce;
  border-color: #3968ce;
}

.horizontal1 {
  float: left;
  width: 50%;
  text-align: right;
  padding-right: 16px;
  margin-top: 8px;
}
.horizontal2 {
  float: left;
  width: 50%;
}
.ml-text-label {
  font-weight: 500;
  font-size: 13px;
  margin-bottom: 6px;
  display: block;
}
.vertical1 {
  margin-top: 8px;
}
.vertical2 {
  margin-right: 8px;
}

:deep .success1 {
  border: 1px solid #1BC5BD !important;
}
:deep .failure1 {
  border: 1px solid #fd397a !important;
}
:deep textarea:disabled {
  background-color: #E4E6EF !important;
  opacity: 1;
}
.error-text {
  color: #fd397a;
  font-style: italic;
  margin-top: 6px;
}
:deep .memlist-text-input {
  display: block;
  width: 100%;
  padding: 0.65rem 1rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #3F4254;
  background-color: #ffffff !important;
  background-clip: padding-box;
  border: 1px solid #E4E6EF;
  border-radius: 0.42rem;
  box-shadow: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  resize: vertical;
}
/* Enhanced variation */
.enhanced-style {
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  font-weight: bold;
}
.enhanced-container {
  padding: 1rem;
}
</style>
